import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { filterSearch } from "@/lib/columnFilters";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { StockClient } from "@/features/warehouse/StockClient";
import { StockCountsClient } from "@/features/warehouse/StockCountsClient";
import { LostFoundClient } from "@/features/warehouse/StockCorrections";
import { PalletDrawer } from "@/features/warehouse/StockClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";
import { listStock, warehouseKeys, type StockSearch } from "@/features/warehouse/warehouse.api";

// Warehouse → Voorraad (docs/design_handoff_wms/README.md §5, docs/wms-datamodel.md §5). Reading is
// wms_stock; blocking and releasing a pallet is wms_stock_write — in the screen, and on the API
// regardless. The open pallet rides in the address, so a link opens it. Behind the switch
// Telverschillen: the blind counts of the floor that did not match (handoff ronde 2 §5).
export const Route = createFileRoute("/_shell/warehouse/stock")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_stock") && !hasModule(context.session, "wms_stock_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): StockSearch & { tab?: "counts" | "lostfound" } => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      warehouse: str(search.warehouse),
      q: str(search.q),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
      pallet: str(search.pallet),
      tab: str(search.tab) === "counts" ? "counts" : str(search.tab) === "lostfound" ? "lostfound" : undefined,
    };
  },
  component: StockScreen,
});

function StockScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEdit = session.roles.includes("admin") || hasModule(session, "wms_stock_write");
  // Afboeken en ophogen change the customer's stock (docs/wms-datamodel.md §18).
  const canBook = session.roles.includes("admin") || hasModule(session, "wms_stock_book");

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  // Another warehouse is other stock: the page, the search and the open pallet start over.
  const choice = useWarehouseChoice(search.warehouse, (code) => updateParams({ warehouse: code, cursor: "", q: "", pallet: "" }));
  const warehouse = choice.current;

  // The open pallet and the switch are not part of the list's query.
  const { pallet: _pallet, tab, ...listSearch } = search;
  const query = useQuery({
    queryKey: warehouseKeys.stock(warehouse?.id ?? "", listSearch),
    queryFn: () => listStock(warehouse!.id, listSearch),
    enabled: warehouse !== undefined && tab === undefined,
    placeholderData: keepPreviousData,
  });

  const tabs = (
    <div className="wl-segment" role="group" aria-label={t("counts.tabsLabel")}>
      {(["pallets", "counts", "lostfound"] as const).map((option) => {
        const active = (tab ?? "pallets") === option;
        return (
          <button key={option} type="button" className={`wl-segment__btn${active ? " is-active" : ""}`} aria-pressed={active} onClick={() => updateParams({ tab: option === "pallets" ? "" : option, pallet: "" })}>
            {t(`counts.tab.${option}`)}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      className="wl-page wl-page--list"
      data-screen-label={tab === "counts" ? "Back-office Warehouse Count Differences" : tab === "lostfound" ? "Back-office Warehouse Lost and Found" : "Back-office Warehouse Stock"}
    >
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : tab === "lostfound" ? (
        <>
          <LostFoundClient
            warehouse={warehouse}
            picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
            tabs={tabs}
            canEdit={canEdit}
            canBook={canBook}
            userId={session.userId}
            onOpen={(id) => updateParams({ pallet: id })}
          />
          {search.pallet && <PalletDrawer id={search.pallet} canEdit={canEdit} canBook={canBook} onClose={() => updateParams({ pallet: "" })} onChanged={() => undefined} />}
        </>
      ) : tab === "counts" ? (
        <StockCountsClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          tabs={tabs}
          canEdit={canEdit}
          canBook={canBook}
          userId={session.userId}
        />
      ) : (
        <StockClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          tabs={tabs}
          items={query.data?.data ?? []}
          pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
          search={search}
          updateParams={updateParams}
          userId={session.userId}
          canEdit={canEdit}
          canBook={canBook}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
        />
      )}
    </div>
  );
}
